/**
 * RouteError — the router's default error component (see createRouter in router.tsx).
 *
 * A code-split route is loaded with React.lazy, so when its chunk is stale after a
 * deploy the failure is caught HERE by TanStack Router's per-route error boundary
 * (not the top-level ErrorBoundary). We detect that specific case and auto-recover
 * by reloading to the fresh build, showing a calm "Updating…" state instead of a
 * scary crash screen. Any other route error falls through to a normal error card.
 *
 * It also REPORTS. This boundary sits below the top-level `ErrorBoundary`, so it
 * is the one that fires whenever a page component throws — and for a long time it
 * reported nowhere. A crash that took out `/sales` in production left no row in
 * `client_errors`, nothing in `/admin/errors`, and printed its message only in a
 * dev build: the only way to learn what had happened was to ask the person looking
 * at the broken screen to open DevTools. Every route crash now lands in telemetry
 * with the route that produced it, and the message is on the card behind a
 * disclosure so support never needs a console again.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isChunkLoadError, reloadForFreshChunks } from '@/shared/utils/chunkReload';
import { reportError } from '@/shared/services/telemetry';

/** The message, for the disclosure and the report. Never a bare "[object Object]". */
function messageOf(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    try { return JSON.stringify(error); } catch { return String(error); }
}

export function RouteError({ error }: { error: unknown }) {
    const chunk = isChunkLoadError(error);
    const detail = messageOf(error);
    /**
     * Start the recovery on the first render and remember whether one is running,
     * so the screen can say what is actually happening. Decided here rather than in
     * an effect: a `setState` in an effect body cascades a render, and the util is
     * guarded against being called twice anyway (`healInFlight`) — which is what
     * makes this safe under StrictMode's double-invoked initialiser. Same shape as
     * SelfHealingNotFound. False means the budget is spent and the button is now
     * the only way out.
     */
    const [healing, setHealing] = useState(() => (chunk ? reloadForFreshChunks() : false));

    useEffect(() => {
        // A stale chunk is an expected consequence of deploying, not a defect —
        // reporting those would bury the real crashes. Everything else is a bug
        // someone has to see.
        if (chunk) return;
        reportError(error, {
            source: 'RouteError',
            route: `${window.location.pathname}${window.location.search}`,
        });
    }, [chunk, error]);

    if (chunk) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
                <RefreshCw
                    className={`h-8 w-8 text-teal-600 ${healing ? 'animate-spin' : ''}`}
                    aria-hidden
                />
                <p className="text-sm text-stone-600 dark:text-stone-300">
                    {healing
                        ? 'Updating to the latest version…'
                        : 'This tab is running an older version of Karr, so this page could not load.'}
                </p>
                {/* The button IS the fix: it clears the old version off this device
                    and reloads. It used to be a plain location.reload(), which the
                    old service worker answered with the same broken build — press
                    it five times, get the same screen five times (22 Aug 2026). */}
                <button
                    onClick={() => setHealing(reloadForFreshChunks({ force: true }))}
                    className="mt-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
                >
                    {healing ? 'Reloading…' : 'Get the latest version'}
                </button>
            </div>
        );
    }

    return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-600" aria-hidden />
            <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">This page hit an error</h2>
            <p className="max-w-md text-sm text-stone-600 dark:text-stone-300">
                Your data is safe — nothing was lost. Try reloading, and if it keeps happening, contact support.
            </p>
            <button
                onClick={() => window.location.reload()}
                className="mt-2 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
            >
                <RefreshCw className="h-4 w-4" aria-hidden />
                Reload
            </button>
            {/* Collapsed by default so a client sees the calm sentence, not a stack
                trace — but one click gets the real reason, in every environment.
                Without this, diagnosing a live crash meant talking someone through
                opening DevTools. Message only, never the stack. */}
            {detail && (
                <details className="mt-4 max-w-xl text-left">
                    <summary className="cursor-pointer text-xs font-medium text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200">
                        Technical details (for support)
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-stone-50 p-3 text-xs whitespace-pre-wrap text-red-700 dark:bg-stone-900 dark:text-red-400">
                        {detail}
                    </pre>
                </details>
            )}
        </div>
    );
}
